import React from 'react';
import * as Yup from 'yup';

import { Window } from '../../contexts';
import { DataHelper, FormHelper, ValidationHelper } from '../../helpers';
import { FieldGroup } from '../../components';
import { AddressFieldset, AjaxForm } from '../../forms';
import { Visible } from '../../core/components';

const fields = [
  'title', 'work_role', 'work_environment', 'company', 'place_id', 'expert_categories'
];
const optionalFields = ['manual_title', 'store_address'];

let validationSchema = ValidationHelper.schema(fields, optionalFields)
validationSchema = validationSchema.shape({
  manual_title: Yup.string().when('title', {
    is: DataHelper.MANUAL_WORK_TITLE,
    then: Yup.string().required('This field is required.'),
    otherwise: Yup.string().optional().nullable(),
  }),
  store_address: Yup.object().when('place_id', {
    is: (val) => val == -1,
    then: Yup.object().shape({
      'country_id': Yup.string().required('This field is required.'),
      'line1': Yup.string().required('This field is required.'),
      'locality': Yup.string().required('This field is required.'),
      'postal_code': Yup.string().required('This field is required.'),
    }),
  }),
  place_id: Yup.string().required(),
});

const processSubmitValues = (values) => {
  const data = { ...values };

  if (data?.store_address?.locality) {
    data.store_address.city = data.store_address.locality;
    delete data.store_address.locality;
  }

  return {
    ...data,
    place_id: data.place_id == -1 ? null : data.place_id,
    store_address: data.store_address || {},
    title: data.title === DataHelper.MANUAL_WORK_TITLE ? data.manual_title : data.title,
  };
};

const WorkForm = ({ user, authentication }) => {
  const initialValues = FormHelper.initialValues([...fields, ...optionalFields], user);

  if (!DataHelper.workTitles.includes(user.title)) {
    initialValues.title = DataHelper.MANUAL_WORK_TITLE;
    initialValues.manual_title = user.title;
  }

  if (!user.store?.google_place_id) {
    initialValues.place_id = -1;
    initialValues.store_address = {
      country_id: user.store?.country?.id,
      locality: user.store?.city,
      line1: user.store?.line1,
      line2: user.store?.line2,
      postal_code: user.store?.postal_code,
      region: user.store?.region,
    };
  } else {
    initialValues.place_id = user.store?.google_place_id;
  }

  const formProps = {
    method: "put",
    url: `/api/users/${user.id}`,
    fields: fields,
    initialValues,
    validationSchema,
    processSubmitValues,
    afterSubmit: (user) => authentication.update(user),
    successTitle: "Changed saved!",
  }

  return (
    <AjaxForm {...formProps}>
      {(props) => {
        const { values } = props;

        return (
          <>
            <div className="row field-group">
              <div className="col-xxl-6">
                <FieldGroup
                  label="Main duty"
                  name="work_role"
                  type="multiselect"
                  mode="switch"
                  className="switch"
                  options={[
                    { id: "management", name: "Management" },
                    { id: "sales", name: "Sales" },
                    { id: "other", name: "Other" }
                  ]}
                />
              </div>

              <div className="col-xxl-6">
                <FieldGroup
                  label="Environment"
                  name="work_environment"
                  type="multiselect"
                  mode="switch"
                  className="switch"
                  options={[
                    { id: "boutique", name: "Boutique" },
                    { id: "department_store", name: "Dept. Store" },
                    { id: "other", name: "Other" }
                  ]}
                />
              </div>
            </div>

            <FieldGroup
                placeholder="Title"
                name="title"
                type="select"
                options={DataHelper.workTitles}
                creatable
            />

            <Visible when={values.title === DataHelper.MANUAL_WORK_TITLE}>
              <FieldGroup
                placeholder="Title"
                name="manual_title"
              />
            </Visible>

            <FieldGroup
              placeholder="Company"
              name="company"
            />

            { values.company.length >= 3 &&
              <FieldGroup
                placeholder="Store"
                name="place_id"
                type="select"
                optionsUrl={`/api/stores/suggestions?company=${values.company}`}
                prependStaticOptions={[{ id: -1, name: DataHelper.MANUAL_COMPANY_ADDRESS }]}
              />
            }

            <Visible when={values.place_id === -1}>
              <AddressFieldset nameObject="store_address" {...props} />
            </Visible>

            <FieldGroup
              label="Work expertise"
              name="expert_categories"
              type="multiselect"
              maxSelections={2}
              options={Window.CATEGORIES}
            />
          </>
        )
      }}
    </AjaxForm>
  )
}

export default WorkForm;
